import { useFormik } from "formik";
import * as Yup from "yup";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Button } from "primereact/button";
import { useState, useRef } from "react";
import ComplaintService from "../../services/complaint/ComplaintService";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import { useNavigate } from "react-router-dom";
import { ImageTemplateDemo } from "../images/file-upload-template";

const severityItems = [
  { name: "High", severityID: 1 },
  { name: "Medium", severityID: 2 },
  { name: "Low", severityID: 3 }
];

const InternalComplaintRegister: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [attachmentId, setAttachmentId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<any>(null);
  const [containerType,setcontainerType] = useState<string>('Complaints');
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      complaintDescription: "",
      severity: "",
      attachmentId: null,
    },
    validationSchema: Yup.object({
      complaintDescription: Yup.string().required("Complaint Description is required"),
      severity: Yup.string().required("Severity is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);

      const payload = {
        complaintDescription: values.complaintDescription,
        severity: values.severity,
        image : imageFile,
        attachmentId: attachmentId || 0,
        createdBy: 0,
        createdDate: new Date().toISOString(),
        updatedBy: 0,
        updatedDate: new Date().toISOString(),
        containerType: containerType
      };

      try {
        const response = await ComplaintService.CreateInternalComplaint(payload);
        if (response && response.isSuccess) {
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Internal complaint registered successfully",
            life: 3000,
          });
          navigate('/internal-complaint-main');
          resetForm();
          setAttachmentId(null);
          setImageFile(null);
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: response?.message || "Failed to register complaint",
            life: 3000,
          });
        }
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "An error occurred while registering complaint",
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    },
  });


  const fieldHoverStyle = {
    border: '1px solid #ced4da',
    borderRadius: '6px',
    transition: 'border 0.2s ease-in-out',
    width: '100%',
    minHeight: '40px'
  };

  // Updated container style for responsiveness
  const formContainerStyle = {
    maxWidth: '1000px',   // Wider max width for larger screens
    width: '90%',       // Fluid width on smaller screens
    margin: '2% auto',
    padding: '20px',
    border: '2px solid gray',
    borderRadius: '6px',
    boxSizing: 'border-box' as const,
    backgroundColor: 'white', // Optional, to make sure form stands out
  };

  const handelfileUpload = (query:any) => {
    const file = query;
    setImageFile(file)
  }

  const handelfileSizeExceeded = (message:string) => {
    toast.current?.show({
        severity: 'info',
        summary: 'InfoError',
        detail: message,
        life: 3000
    });
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div style={formContainerStyle}>
        <h5 className='border-round mb-3 order-top-color p-2 panel-header-text text-center shadow-3' style={{backgroundColor: '#007A33', color: 'white', fontWeight: 'bold'}}>
          INTERNAL COMPLAINT REGISTRATION
        </h5>
        <form onSubmit={formik.handleSubmit}>
          <div className="p-fluid formgrid grid">
            <div className="field col-12 mb-3 px-3">
              <label htmlFor="complaintDescription" style={{ fontWeight: 'bold' }}>
                Complaint Description <span style={{ color: "red", fontSize: "24px" }}>*</span>
              </label>
              <div style={fieldHoverStyle}>
                <InputTextarea
                  id="complaintDescription"
                  name="complaintDescription"
                  value={formik.values.complaintDescription}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                    formik.setFieldValue('complaintDescription', cleanedValue);
                  }}
                  onBlur={formik.handleBlur}
                  rows={4}
                  className={`w-full ${formik.touched.complaintDescription && formik.errors.complaintDescription ? "p-invalid" : ""}`}
                  placeholder="Enter complaint description"
                  style={{ border: 'none', outline: 'none', width: '100%', padding: '0.75rem', fontSize: '1rem' }}
                />
              </div>
              {formik.touched.complaintDescription && formik.errors.complaintDescription && (
                <small className="p-error">{formik.errors.complaintDescription}</small>
              )}
            </div>

            <div className="field col-12 md:col-6 mb-3 px-3">
              <label htmlFor="severity" style={{ fontWeight: 'bold' }}>
                Severity <span style={{ color: "red", fontSize: "24px" }}>*</span>
              </label>
              <div style={fieldHoverStyle}>
                <Dropdown
                  placeholder="Select Severity"
                  name="severity"
                  id="severity"
                  options={severityItems}
                  optionLabel="name"
                  optionValue="name"
                  value={formik.values.severity}
                  onChange={(e: DropdownChangeEvent) => {
                    formik.setFieldValue("severity", e.value);
                  }}
                  className={classNames("w-full", {
                    "p-invalid": formik.touched.severity && formik.errors.severity,
                  })}
                  style={{ border: 'none', outline: 'none', width: '100%' }}
                />
              </div>
              {formik.touched.severity && formik.errors.severity && (
                <small className="p-error">{formik.errors.severity}</small>
              )}
            </div>
            <ImageTemplateDemo onFileUpload={handelfileUpload} handelFileSize={handelfileSizeExceeded}/>
            <div className="field col-12 flex flex-wrap gap-3 justify-content-end px-3">
              <Button
                type="button"
                label="Reset"
                className="p-button-secondary"
                onClick={() => {
                  formik.resetForm();
                  setAttachmentId(null);
                  setImageFile(null);
                }}
                disabled={loading}
              />
              <Button
                type="submit"
                label="Register Complaint"
                className="p-button-primary"
                loading={loading}
                disabled={loading}
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default InternalComplaintRegister;
